import { useEffect } from 'react'

import { ENTITY_TYPES } from '@sales-center/common'

import { useMissingAutoCloseRequiredFields } from '@user/query/useMissingAutoCloseRequiredFields'
import { useAppDispatch } from '@user/store/hooks/useAppDispatch'
import { useAppSelector } from '@user/store/hooks/useAppSelector'
import {
  getSalesFunnelList,
  selectSalesFunnelById,
} from '@user/store/salesFunnel'

import { useLeadOrOppDetails } from './useLeadOrOppDetails'
import {
  resolveAgreementAutoClose,
  resolveAutoCloseFunnelId,
} from './agreementAutoCloseGate'

export function useLeadAutoCloseIndicator(
  entityId: number,
  entityType: ENTITY_TYPES,
) {
  const dispatch = useAppDispatch()
  const isLead = entityType === ENTITY_TYPES.Lead

  // WWM-18141: both messages are funnel-driven, so the Opportunity needs its funnel
  // too — not just the Lead. AgreementFormPacketDialog loads whichever detail record
  // applies via useGetEntityDetailsOnMount, so this works on every surface the dialog
  // opens from (detail page, map view drawer, Manage Agreements widget).
  const entityDetail = useLeadOrOppDetails(entityType)
  const salesFunnelId = resolveAutoCloseFunnelId(entityDetail, entityId)

  const salesFunnel = useAppSelector((state) =>
    salesFunnelId !== undefined
      ? selectSalesFunnelById(state, salesFunnelId)
      : undefined,
  )

  useEffect(() => {
    if (salesFunnelId !== undefined && !salesFunnel) {
      dispatch(getSalesFunnelList(false))
    }
  }, [dispatch, salesFunnelId, salesFunnel])

  const autoCloseLeadActive = isLead && Boolean(salesFunnel?.autoCloseWinLeads)

  const { data: missingFieldIds } = useMissingAutoCloseRequiredFields(
    entityId,
    autoCloseLeadActive,
  )

  return resolveAgreementAutoClose({
    isLead,
    autoCloseWinLeads: Boolean(salesFunnel?.autoCloseWinLeads),
    autoCloseWinOpportunities: Boolean(salesFunnel?.autoCloseWinOpportunities),
    missingFieldIds,
  })
}
